import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ClipboardList,
  Flag,
  HeartPulse,
  Megaphone,
  LineChart,
  Layers,
  BadgeCheck,
} from "lucide-react";
import { Reveal, Chapter, EASE } from "./primitives";
import { useLanguage } from "../../i18n/LanguageContext";

const TILE_ICONS = [ClipboardList, Flag, HeartPulse, Megaphone];
const TILE_IMAGES = ["/images/tile-coach.jpg", "/images/tile-referee.jpg", "/images/tile-physio.jpg", "/images/tile-event.jpg"];
const BAND_ICONS = [ClipboardList, Flag, HeartPulse, LineChart, Layers];

export const RoleTile = ({ index, icon: Icon, name, image, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    whileHover={{ y: -6 }}
    className="group relative flex aspect-[3/4] flex-col justify-between overflow-hidden bg-kn-navy p-6"
    data-testid={`role-tile-${name.toLowerCase().replace(/\s/g, "-")}`}
  >
    <img src={image} alt={name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-kn-navy via-kn-navy/55 to-kn-navy/20" />
    <div className="relative flex items-start justify-between">
      <span className="font-mono text-xs text-kn-cyan">{index}</span>
      <BadgeCheck className="h-4 w-4 text-white/40 transition-colors duration-500 group-hover:text-kn-cyan" strokeWidth={1.25} />
    </div>
    <Icon
      className="relative mx-auto h-14 w-14 text-white/85 transition-transform duration-500 group-hover:scale-110"
      strokeWidth={0.9}
    />
    <div className="relative">
      <span className="block h-px w-8 bg-kn-cyan/50 transition-all duration-500 group-hover:w-14" />
      <p className="mt-3 font-serif text-xl italic text-white">{name}</p>
    </div>
  </motion.div>
);

export const RoleTiles = () => {
  const { t } = useLanguage();
  return (
  <section className="relative bg-white py-24 lg:py-32" data-testid="role-tiles-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Chapter index="00" label={t.tiles.chapter} />
        <h2 className="mt-6 max-w-2xl text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
          {t.tiles.heading}
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        {t.tiles.roles.map((r, i) => (
          <RoleTile key={r.index} index={r.index} icon={TILE_ICONS[i]} name={r.name} image={TILE_IMAGES[i]} delay={i * 0.1} />
        ))}
      </div>
    </div>
  </section>
  );
};

export const RoleBand = () => {
  const { t } = useLanguage();
  return (
  <section className="border-y border-kn-navy/10 bg-kn-ice py-14" data-testid="role-band">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <div className="flex flex-wrap items-stretch justify-between gap-y-8">
          {t.band.units.map((label, i) => {
            const Icon = BAND_ICONS[i];
            return (
            <div
              key={label}
              className={`flex flex-1 min-w-[140px] items-center justify-center gap-3 px-4 ${
                i > 0 ? "border-l border-kn-navy/10" : ""
              }`}
              data-testid={`role-band-${i}`}
            >
              <Icon className="h-5 w-5 text-kn-blue" strokeWidth={1.25} />
              <span className="text-sm font-medium text-kn-navy">{label}</span>
            </div>
            );
          })}
        </div>
        <p className="mt-10 text-center font-mono text-[11px] tracking-[0.28em] uppercase text-kn-navy/45">
          {t.band.caption}
        </p>
      </Reveal>
    </div>
  </section>
  );
};

export const ImageryBand = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  return (
    <section ref={ref} className="relative h-[60vh] overflow-hidden lg:h-[75vh]" data-testid="imagery-band">
      <motion.img
        src="/images/band-sprinklers.jpg"
        alt="Stadium at night"
        loading="lazy"
        style={{ y }}
        className="absolute -top-[15%] left-0 h-[130%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-kn-navy via-kn-navy/25 to-kn-navy/55" />
      <div className="relative mx-auto flex h-full max-w-7xl items-end px-6 pb-14 lg:px-10">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.32em] uppercase text-kn-cyan">CYRA Krida</p>
          <p className="mt-3 max-w-xl font-serif text-3xl italic leading-snug text-white sm:text-4xl" data-testid="imagery-caption">
            {t.imagery.caption}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
